import { AfterViewInit, Component, inject, OnInit } from '@angular/core';
import { EventOriginEnum, NavigateToEnum } from '@app/core/enums/analytics/analytics-value.enum';
import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { Equipment, RelatedEquipment } from '@app/core/model/entities/equipments/equipment';
import { EquipmentsService } from '@app/features/main/views/equipments/equipments.service';
import {
  EntityChipsFieldBuilderComponent
} from '@app/shared/components/fields/chips-field-builder/entity-chips-field-builder/entity-chips-field-builder';
import { eq, nEq, textContains } from '@app/shared/extra/filter-model-builder';
import { getLocale } from '@app/shared/extra/utils';
import { Filter, SelectParams } from '@app/shared/grid/datasource-utils';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  selector: 'equipments-chips-field-builder',
  templateUrl: './entity-chips-field-builder.html',
  styleUrls: ['../chips-field-builder.component.scss'],
  standalone: false
})
export class EquipmentsChipsFieldBuilderComponent extends EntityChipsFieldBuilderComponent<RelatedEquipment> implements OnInit, AfterViewInit {
  private equipmentsService = inject(EquipmentsService);

  protected readonly entityType = EntityTypeEnum.EQUIPMENT;
  protected noRelatedEntitiesMessage = 'LABEL.NO_RELATED_EQUIPMENTS';

  /**
   * Load equipments next page.
   * @param startingIndex Start of the page to load.
   * @return Observable<RelatedEntity[]> A list of RelatedEntity matching the form filters.
   */
  protected loadEntitiesNextPage(startingIndex: number): Observable<RelatedEquipment[]> {
    // Do not fetch next page if the module is not allowed or if there is no current asset
    if (!this.assetTypeAllowsEntityModule || !this.assetChanged$.value) return of([]);

    // Build filters
    const filters: Filter[] = [];

    if (this.assetChanged$.value) {
      filters.push(eq('assetId', this.assetChanged$.value));
    }

    if (this.form.controls.field.value) {
      filters.push(textContains('computedProperties.displayName', this.form.controls.field.value));
    }

    if (this.entity instanceof Equipment) {
      filters.push(nEq('id', this.entity.id));
    }

    // Build Select Params
    const selectParams: SelectParams = {
      locale: getLocale(),
      sorts: [
        {
          field: 'lastChangeDate',
          direction: 'DESC'
        }
      ],
      filters,
      start: startingIndex,
      count: this.appConfig.PREFERRED_REQUEST_PAGE_SIZE
    };

    // Load equipments page
    return this.equipmentsService.loadEquipmentsPage(selectParams)
      .pipe(map(equipmentsPage => equipmentsPage.map(equipment => equipment.toRelatedEquipment())));
  }

  /**
   * Navigate to Equipment sheet
   * @param relatedEntityId Equipment ID
   */
  public async navigateToRelatedEntitySheet(relatedEntityId: string): Promise<void> {
    this.analyticsService.trackNavigationEvent(
      EventOriginEnum.FIELD_CLICK,
      NavigateToEnum.SHEET,
      EntityTypeEnum.EQUIPMENT,
      relatedEntityId
    );
    await this.equipmentsService.navigateToEquipmentSheet(relatedEntityId);
  }
}
